import {
  readStorageItem,
  readStorageJSON,
  removeStorageItem,
  resolveStoredAuthToken as resolveSharedStoredAuthToken,
  safeParseJSON,
  writeStorageItem,
  writeStorageJSON,
} from '@liquid-spirit/api/storage';

export const STORAGE_VERSION = 1;

export const STORAGE_KEYS = Object.freeze({
  AUTH_TOKEN: 'authToken',
  AUTH_REFRESH_TOKEN: 'authRefreshToken',
  AUTH_USER: 'authUser',
  LEGACY_AUTH_TOKEN: 'token',
  LEGACY_REFRESH_TOKEN: 'refreshToken',
  LEGACY_USER: 'user',
  LAST_PROTECTED_ROUTE: 'ls:last-protected-route',
  FORM_PROGRESS: 'ls:form-progress',
  FORM_RECOVERY_PENDING: 'ls:form-recovery-pending',
  RECOVERY_BANNER_SHOWN: 'ls:recovery-banner-shown',
  CHUNK_ERROR_LAST_RELOAD: 'ls_chunk_error_last_reload',
});

export const STORAGE_VERSIONS = Object.freeze({
  FORM_PROGRESS: STORAGE_VERSION,
});

const VERSION_FIELD = '__lsv';
const VALUE_FIELD = 'value';

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

export const readVersionedStorage = (
  key,
  {
    area = 'local',
    fallback = null,
    expectedVersion = null,
  } = {},
) => {
  const parsed = readStorageJSON(key, { area, fallback });
  if (
    parsed &&
    typeof parsed === 'object' &&
    !Array.isArray(parsed) &&
    hasOwn(parsed, VERSION_FIELD) &&
    hasOwn(parsed, VALUE_FIELD)
  ) {
    if (expectedVersion != null && parsed[VERSION_FIELD] !== expectedVersion) {
      return fallback;
    }
    return parsed[VALUE_FIELD];
  }
  return parsed;
};

export const writeVersionedStorage = (
  key,
  value,
  { area = 'local', version = STORAGE_VERSION } = {},
) => {
  if (value === undefined) {
    return removeStorageItem(key, { area });
  }
  return writeStorageJSON(
    key,
    {
      [VERSION_FIELD]: version,
      [VALUE_FIELD]: value,
    },
    { area },
  );
};

export const resolveStoredAuthToken = (override = undefined) => {
  return resolveSharedStoredAuthToken({
    explicitToken: override,
    authTokenKey: STORAGE_KEYS.AUTH_TOKEN,
    legacyTokenKey: STORAGE_KEYS.LEGACY_AUTH_TOKEN,
  });
};

// Single place that persists the access token: keep the canonical copy in
// sessionStorage and clear any stale local/legacy copies. Used by both the login
// seed (useAuthBootstrap) and AuthContext's reactive sync so there is exactly one
// token-write path.
export const persistAuthToken = (token) => {
  if (token) {
    writeStorageItem(STORAGE_KEYS.AUTH_TOKEN, token, { area: 'session' });
  } else {
    removeStorageItem(STORAGE_KEYS.AUTH_TOKEN, { area: 'session' });
  }
  removeStorageItem(STORAGE_KEYS.AUTH_TOKEN);
  removeStorageItem(STORAGE_KEYS.LEGACY_AUTH_TOKEN);
};

export {
  readStorageItem,
  readStorageJSON,
  removeStorageItem,
  safeParseJSON,
  writeStorageItem,
  writeStorageJSON,
};
